import React, { useMemo } from 'react';
import { Layout } from '@apps/card/components/Layout';
import type { PriceUpTo } from '@apps/card/components/PriceSummary';
import PriceSummary from '@apps/card/components/PriceSummary';
import { useCustomizationContext } from '../../useCardCustomization';
import ReceiptBlock from '@apps/card/components/Blocks/ReceiptBlock';
import { useNavigationContext } from '../../useNavigationState';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { maybeApplyCallback } from '@shared/utils/maybeApplyCallback';
import type { NavigationStep, PrintNavigationStep } from '../../CardCustomizer.types';
import type { DraftJson } from '../../DraftJson.types';
import type { CardPaymentMethodFragment } from '@graphql/queries/registry.graphql.generated';
import { StationeryDraftFormat } from '@graphql/generated-base';
import { Box, TextV2 } from '@withjoy/joykit';
import { capitalize } from '@shared/joykit/packages/core/utils';
import { getDimensions } from '@apps/card/utils';
import { templateToTitle } from '@apps/card/utils/templateToTitle';
import { formatEnvelopeDesignName } from '@apps/card/routes/CardCustomizer/steps/Envelope/envelopeTemplateUtils';
import { useCardCustomizerTelemetry } from '../../CardCustomizer.telemetry';
import { PromoCodeInput } from '../PromoCodeInput';
import { usePromotionConfig } from '../../usePromotionConfig';
import type { CardPromotion } from '@apps/card/components/PriceSummary/PriceSummary.controller';
import { useCardCustomizerConfig } from '@apps/card/hooks/useCardCustomizedConfig';
import { useStationeryTemplateCategoryTranslations } from '@apps/card/hooks/useStationeryTemplateCategoryTranslations';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useRegisterAttributeLinks } from '@apps/card/components/ProductUIConfig/utils/state/state.mapping';
import { useTranslation } from '@shared/core';

interface SidePanelPriceBreakdownProps {
  title?: string;
  paidWith?: CardPaymentMethodFragment | null;
  config?: DraftJson;
  disablePromoCodeInput?: boolean;
  promotionOverride?: Maybe<CardPromotion>;
  taxInMinorUnits?: Maybe<number>;
}

const VISIBILITY_CONFIG: Record<PriceUpTo, Partial<Record<PriceUpTo, boolean>>> = {
  cardFront: {
    cardFront: true,
  },
  cardBack: {
    cardFront: true,
    cardBack: true,
  },
  envelope: {
    cardFront: true,
    cardBack: true,
    envelope: true,
  },
  total: {
    cardFront: true,
    cardBack: true,
    envelope: true,
    total: true,
  },
};

/**
 * Standalone envelopes: only the envelope block, and the total once Review is reached.
 *
 * A separate map rather than a filter over the one above, because that one is cumulative — the
 * envelope step reveals cardFront and cardBack as well — and an envelope draft sits on the
 * envelope step for its whole life. Left alone it would render two empty card blocks, the first
 * of them titled with the carrier template's category and design name, which no customer should
 * ever see. The `cardFront`/`cardBack` keys are absent rather than false so the render guards
 * read falsy without either block ever being considered.
 */
const ENVELOPE_VISIBILITY_CONFIG: Record<PriceUpTo, Partial<Record<PriceUpTo, boolean>>> = {
  cardFront: {
    envelope: true,
  },
  cardBack: {
    envelope: true,
  },
  envelope: {
    envelope: true,
  },
  total: {
    envelope: true,
    total: true,
  },
};

export const SidePanelPriceBreakdownBlock = (props: SidePanelPriceBreakdownProps) => {
  const { tq } = useTranslation('stationery');
  const { disablePromoCodeInput, promotionOverride, taxInMinorUnits } = props;
  const [config] = useCustomizationContext(draft => draft.config);
  const [templateCategory] = useCustomizationContext(draft => draft.stationeryTemplateCategory);
  const [template] = useCustomizationContext(draft => draft.template);
  const getCategoryName = useStationeryTemplateCategoryTranslations('stationeryTemplateCategoryToTile');
  const categoryName = getCategoryName(templateCategory);
  const { goToStep, currentStep, steps } = useNavigationContext();
  const { customizationButtonInteracted } = useCardCustomizerTelemetry();
  const { currentPromotion, isEnabled: isCustomPromoEnabled } = usePromotionConfig();
  const [cardDimensions] = useCustomizationContext(draft => getDimensions(draft.config.card));
  const { activeProduct, attributeStates } = useStationeryProductPricingContext();
  const { helpers } = useCardCustomizerConfig(StationeryDraftFormat.print, templateCategory, cardDimensions, activeProduct.productType);

  const priceUpTo = useMemo((): PriceUpTo => {
    // Reversing the keys to find the last completed step
    const lastCompletedStep = (Object.keys(steps) as Array<PrintNavigationStep>).reverse().find(step => {
      // Need to check if the step is the current step or if it has been completed
      return step === currentStep || steps[step].hasCompleted;
    });

    const target = lastCompletedStep || currentStep;

    switch (target) {
      case 'cardFront':
        return 'cardFront';
      case 'cardBack':
        return 'cardBack';
      case 'envelope':
        return 'envelope';
      case 'review':
        return 'total';
      default:
        return 'total';
    }
  }, [currentStep, steps]);

  // Keyed on the product, not on `printEnvelopeProduct`: the flag gates the entry points, while
  // this decides what an order that already exists renders as.
  const isEnvelopeProduct = activeProduct.productType === 'envelope';
  const visibiltyConfig = (isEnvelopeProduct ? ENVELOPE_VISIBILITY_CONFIG : VISIBILITY_CONFIG)[priceUpTo];

  const handleOnEditClick = useEventCallback((step: 'cardFront' | 'cardBack' | 'envelope') => {
    customizationButtonInteracted({ property: 'orderSummaryBlock_goToStep', value: step });
    goToStep(step);
  });

  const isContainedDesign = currentStep === 'review' || currentStep === 'postCheckout';
  const canMakeEdits = currentStep !== 'postCheckout';
  const isCurrentStep = (step: NavigationStep) => step === currentStep;

  const cardConfig = props.config || config;
  useRegisterAttributeLinks(attributeStates.addAttribute, activeProduct);
  const attributes = attributeStates.getFullState(cardConfig);

  return (
    <Layout.SidePanelBlock
      backgroundColor={isContainedDesign ? undefined : 'mono2'}
      paddingY={isContainedDesign ? undefined : 7}
      marginTop={'auto'}
    >
      <PriceSummary
        salesTax={taxInMinorUnits}
        templateCategory={templateCategory}
        title={typeof props.title === 'undefined' ? tq`Order Summary` : props.title}
        cardConfig={cardConfig}
        attributes={attributes}
        priceUpTo={priceUpTo}
        promotion={promotionOverride ?? currentPromotion}
        cardSize={cardDimensions}
      >
        {summary => (
          <>
            {visibiltyConfig.cardFront && (
              <ReceiptBlock
                title={categoryName}
                subTitle={templateToTitle(template)}
                onEditButtonClick={maybeApplyCallback(canMakeEdits && !isCurrentStep('cardFront'), () => handleOnEditClick('cardFront'))}
                lines={summary.card}
              />
            )}
            {visibiltyConfig.cardBack && !!summary.back.length && (
              <ReceiptBlock
                title={tq`Back`}
                onEditButtonClick={maybeApplyCallback(canMakeEdits && !isCurrentStep('cardBack'), () => handleOnEditClick('cardBack'))}
                lines={summary.back}
              />
            )}
            {visibiltyConfig.envelope && helpers.isStepEnabled('envelope') && (
              <ReceiptBlock
                title={tq`Envelope`}
                // Only for the standalone envelope product — the chosen design name, read from the
                // draft, not the carrier template (which is deliberately generic, §2.1 of the plan).
                // Card orders' own envelope block is unaffected; this does not touch their behavior.
                subTitle={isEnvelopeProduct ? formatEnvelopeDesignName(cardConfig.card.envelope.design) : undefined}
                onEditButtonClick={maybeApplyCallback(canMakeEdits && !isCurrentStep('envelope'), () => handleOnEditClick('envelope'))}
                lines={summary!.envelope}
              />
            )}
            {visibiltyConfig.total && (
              <ReceiptBlock
                title={tq`Shipping Method`}
                lines={summary!.shipping}
              />
            )}
            {!!taxInMinorUnits && (
              <ReceiptBlock
                title={tq`Tax`}
                lines={summary.tax}
              />
            )}
          </>
        )}
      </PriceSummary>
      {!disablePromoCodeInput && isCustomPromoEnabled && (
        <Box marginTop={7}>
          <PromoCodeInput />
        </Box>
      )}
      {props.paidWith && <TextV2 typographyVariant="label2">{tq`Paid with ${capitalize(props.paidWith.card?.brand).text} ${props.paidWith.card?.last4}`}</TextV2>}
    </Layout.SidePanelBlock>
  );
};
